import { useWindowSize } from "@react-hookz/web";
import { useState } from "react";
import { Button, SearchInput } from "@/components/atoms";
import { useOperator, usePagination, useSearch, useSubscriptionAssignment, useSubscriptionPlanLimits } from "@/hooks";
import { useVehicleTypeMigrationCheck } from "@/hooks/useVehicleTypeMigrationCheck";
import { useGetVehiclesQuery } from "@/redux/apis/vehicle/vehicleApi";
import { clsx } from "@/utils";
import { AddVehicleEntry } from "../AddVehicle/AddVehicleEntry";
import { VehicleTypesSetupModal } from "../VehicleTypesSetupModal";
import { VehicleFilters } from "../features/Filters/VehicleFilters";
import { VehicleLimitReachedModal } from "./modals/VehicleLimitReachedModal";

export const MyVehiclesActionMenu = () => {
  const [search, setSearch] = useSearch();
  const { clearPagination } = usePagination();
  const [isSearchActive, setIsSearchActive] = useState(false);
  const [showLimitModal, setShowLimitModal] = useState(false);
  const [showAddVehicleModal, setShowAddVehicleModal] = useState(false);
  const [showSetupModal, setShowSetupModal] = useState(false);
  const { needsMigration: needsTypeMigration, valid: typesValid, total: typesTotal } = useVehicleTypeMigrationCheck();
  const { id: operatorId } = useOperator();
  const { subscriptionStatus } = useSubscriptionAssignment();

  // Get current vehicle count (only migrated vehicles count toward limit)
  const { data: vehiclesData } = useGetVehiclesQuery(
    {
      owner_uuid: operatorId ?? "",
      needs_type_migration: false,
      page_size: "1",
      page: "1",
    },
    {
      skip: !operatorId,
    }
  );

  const { getPlanLimitsById, shouldShowLimitIndicatorById } = useSubscriptionPlanLimits();
  const subscriptionId = subscriptionStatus.baseSubscription?.subscription.subscriptionId ?? "";
  const limits = getPlanLimitsById(subscriptionId);
  const showLimitCheck = shouldShowLimitIndicatorById(subscriptionId);
  const vehicleCount = vehiclesData?.page.total ?? 0;
  const isAtVehicleLimit = showLimitCheck && vehicleCount >= limits.maxVehicles;

  const handleSearch = (value?: string) => {
    if (!value) setIsSearchActive(false);

    clearPagination();
    setSearch(value);
  };

  if (!isSearchActive && !search) {
    return (
      <>
        <div className="flex flex-1 items-center justify-end gap-0 xl:gap-2 ">
          <Button
            className="max-md:[&>span]:hidden"
            variant="tertiary"
            size="sm"
            startIcon="SearchNormal1"
            onClick={() => setIsSearchActive(true)}
          >
            Search
          </Button>
          <FilterButton />
          <Button
            className="max-md:[&>span]:hidden"
            variant="primary"
            size="sm"
            startIcon="Add"
            onClick={() => {
              if (needsTypeMigration) setShowSetupModal(true);
              else if (isAtVehicleLimit) setShowLimitModal(true);
              else setShowAddVehicleModal(true);
            }}
          >
            Add Vehicle
          </Button>
        </div>
        <AddVehicleEntry open={showAddVehicleModal} onClose={() => setShowAddVehicleModal(false)} />
        <VehicleLimitReachedModal isOpen={showLimitModal} onClose={() => setShowLimitModal(false)} limit={limits.maxVehicles} />
        <VehicleTypesSetupModal open={showSetupModal} onClose={() => setShowSetupModal(false)} valid={typesValid} total={typesTotal} />
      </>
    );
  }

  return (
    <div className="flex flex-1 items-center justify-end gap-2">
      <SearchInput
        search={search}
        onSearch={handleSearch}
        autoFocus
        placeholder="Search Number Plate, Make and Model"
        className={clsx("w-full max-w-sm p-0 sm:w-fit [&>input]:transition-size", isSearchActive && "[&>input]:w-[280px]")}
        size="sm"
        onClear={() => setIsSearchActive(false)}
      />
    </div>
  );
};

const FilterButton = () => {
  const { width } = useWindowSize();

  return width >= 768 ? <VehicleFilters.Button /> : <VehicleFilters.DrawerButton />;
};
